/**
 * The logo panel on a brand.
 *
 * Not decoration, and not the same thing as a product photo. A consumer builds
 * its manufacturer list from the source's own directory (TecDoc's `getBrands`)
 * and renders each entry's logo from there — so a brand outside the licence
 * (SISTEMCAR, TAB) has no entry at all, and one inside it may have an entry with
 * no image. What is uploaded here is what fills both gaps: we publish the brand
 * into that directory carrying this file.
 *
 * One logo per brand, so uploading replaces rather than appends.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../lib/api';
import type { BrandLogo as BrandLogoRow } from '../lib/types';
import { ErrorBox, Switch } from '../ui';

export default function BrandLogo({ brandId, onChanged }: { brandId: number; onChanged?: () => void }) {
  const { request, base } = useApi();
  const [logo, setLogo] = useState<BrandLogoRow | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const r = await request<BrandLogoRow>('GET', `/v1/brands/${brandId}/logo`);
    setLogo(r.ok ? r.data : null);
  }, [brandId, request]);

  useEffect(() => {
    void load();
  }, [load]);

  async function upload(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    setBusy(true);
    setError('');
    const form = new FormData();
    form.append('file', file);
    const r = await request<{ error?: string }>('POST', `/v1/admin/brands/${brandId}/logo`, form);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
    if (!r.ok) {
      setError(r.data?.error ?? `upload failed (HTTP ${r.status})`);
      return;
    }
    await load();
    onChanged?.();
  }

  async function toggle(injected: boolean) {
    await request('PATCH', `/v1/admin/brands/${brandId}/logo`, { injected });
    await load();
  }

  async function remove() {
    await request('DELETE', `/v1/admin/brands/${brandId}/logo`);
    await load();
    onChanged?.();
  }

  // Content-addressed and public, so it needs no token and can be rendered
  // straight from somebody else's page. The API returns an absolute URL — the CDN
  // pull zone, or its own origin — but an older API returns the bare path.
  const src = logo ? (/^https?:\/\//i.test(logo.url) ? logo.url : `${base.replace(/\/$/, '')}${logo.url}`) : '';

  return (
    <div className="flex flex-col gap-2">
      <div className="text-[11.5px] text-faint">
        Logo — published into the manufacturer directory we inject, for brands the source does not carry or carries
        without an image.
      </div>
      {error && <ErrorBox>{error}</ErrorBox>}
      <div className="flex items-center gap-3">
        {logo ? (
          <a href={src} target="_blank" rel="noreferrer">
            <img
              src={src}
              alt=""
              className="h-12 w-24 rounded border border-line bg-panel2 object-contain p-1"
            />
          </a>
        ) : (
          <div className="flex h-12 w-24 items-center justify-center rounded border border-dashed border-line text-[11px] text-faint">
            no logo
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          className="input"
          style={{ width: 'auto' }}
          onChange={(e) => void upload(e.target.files)}
          disabled={busy}
        />
        {logo && (
          <>
            <Switch on={logo.injected} onChange={(v) => void toggle(v)}>
              <span className="text-[11px]">inject</span>
            </Switch>
            <button className="btn btn-sm btn-danger" onClick={() => void remove()}>
              Delete
            </button>
          </>
        )}
      </div>
    </div>
  );
}
